import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import type { Company, EbInvoiceMatch, InvoiceDetail as Detail } from '@aviaconsole/shared';
import { capi, companyUrl } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { formatDate, formatEur } from '../lib/format';
import { useToast } from '../components/Toast';
import { BackLink, Button, Card, Chip, ConfirmDialog, Field, Input, Loading, Modal, PageHeader, invoiceStatusLabel, invoiceTone, vatTreatmentLabel } from '../components/ui';
import { InvoiceEditor } from './InvoiceNew';
import { PdfFrame, PdfPreview } from '../components/PdfPreview';

/** Whether the archived PDF is shown next to the invoice data — a per-browser preference. */
const PDF_PANE_KEY = 'ld.invoicePdfPane';
function readPdfPane(): boolean { try { return localStorage.getItem(PDF_PANE_KEY) === '1'; } catch { return false; } }

export function InvoiceDetail() {
  const { id } = useParams();
  const { company } = useAuth();
  const { t, s, lang } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const key = ['invoice', company?.id, id];
  const q = useQuery({ queryKey: key, queryFn: () => capi<Detail>(`/invoices/${id}`) });
  const inv = q.data;
  /** Shared with Settings.tsx's query — cheap, and tells us whether e-Boekhouden is actually connected. */
  const settings = useQuery({ queryKey: ['settings', company?.id], queryFn: () => capi<Company>('/settings') });
  const today = new Date().toISOString().slice(0, 10);
  const [paidAt, setPaidAt] = useState(today);
  const [amount, setAmount] = useState('');
  const [preview, setPreview] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pdfPane, setPdfPaneState] = useState(readPdfPane);
  const setPdfPane = (on: boolean) => { setPdfPaneState(on); try { localStorage.setItem(PDF_PANE_KEY, on ? '1' : '0'); } catch { /* preference only */ } };
  /** Which irreversible step is waiting for a yes. */
  const [confirming, setConfirming] = useState<'edit' | 'delete' | null>(null);
  const [linking, setLinking] = useState(false);

  const act = useMutation({
    mutationFn: ({ path, json }: { path: string; json?: unknown }) => capi<Detail>(`/invoices/${id}${path}`, { method: 'POST', json: json ?? {} }),
    onSuccess: (data) => { qc.setQueryData(key, data); qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); toast.push(t('done')); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const del = useMutation({
    mutationFn: () => capi(`/invoices/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoices'] }); navigate('/invoices'); },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  if (!inv) return <Loading />;
  const openCents = inv.totalCents - inv.paidCents;
  const isDraft = inv.status === 'draft';
  const isOpen = !isDraft && inv.status !== 'paid' && inv.status !== 'cancelled';

  if (isDraft) {
    return (
      <>
        <PageHeader crumb={t('invDetailCrumb')} title={s('Draft')} sub={t('invDetailDraftSub')}
          actions={<><Button variant="danger" icon="fas fa-trash-can" onClick={() => setConfirming('delete')}>{t('deleteDraft')}</Button><Button icon="fas fa-eye" onClick={() => setPreview(true)}>{t('showConcept')}</Button><Button icon="fas fa-file-circle-check" onClick={() => act.mutate({ path: '/send', json: { mail: false } })} disabled={act.isPending}>{t('finaliseNoMail')}</Button><Button variant="primary" icon="fas fa-paper-plane" onClick={() => act.mutate({ path: '/send' })} disabled={act.isPending}>{t('finalise')}</Button></>} />
        <InvoiceEditor existing={inv} />
        {preview && <PdfPreview url={companyUrl(`/invoices/${id}/preview.pdf`)} title={t('conceptTitle')} onClose={() => setPreview(false)} />}
        {confirming === 'delete' && (
          <ConfirmDialog title={t('confirmDeleteDraftTitle')} confirmLabel={t('deleteDraft')} cancelLabel={t('cancel')} danger pending={del.isPending} onConfirm={() => del.mutate()} onClose={() => setConfirming(null)}>
            {t('confirmDeleteDraftBody')}
          </ConfirmDialog>
        )}
      </>
    );
  }

  const dialogs = (
    <>
      {confirming === 'delete' && (
        <ConfirmDialog title={t('confirmDeleteInvoiceTitle', { number: inv.number })} confirmLabel={t('deleteInvoice')} cancelLabel={t('cancel')} danger pending={del.isPending} onConfirm={() => del.mutate()} onClose={() => setConfirming(null)}>
          <p style={{ margin: 0 }}>{t('confirmDeleteInvoiceBody')}</p>
          {inv.ebInvoiceNumber && <p style={{ margin: '0.6rem 0 0' }}>{t('confirmDeleteInvoiceEb', { number: inv.ebInvoiceNumber })}</p>}
        </ConfirmDialog>
      )}
      {confirming === 'edit' && (
        <ConfirmDialog title={t('confirmEditInvoiceTitle', { number: inv.number })} confirmLabel={t('editInvoice')} cancelLabel={t('cancel')} onConfirm={() => { setConfirming(null); setEditing(true); }} onClose={() => setConfirming(null)}>
          <p style={{ margin: 0 }}>{t('confirmEditInvoiceBody')}</p>
          {inv.ebInvoiceNumber && <p style={{ margin: '0.6rem 0 0' }}>{t('confirmEditInvoiceEb', { number: inv.ebInvoiceNumber })}</p>}
        </ConfirmDialog>
      )}
    </>
  );

  if (editing) {
    return (
      <>
        <PageHeader crumb={t('invDetailCrumb')} title={inv.number} sub={t('invDetailEditSub', { number: inv.number })}
          actions={<><Button icon="fas fa-xmark" onClick={() => setEditing(false)}>{t('cancel')}</Button><Chip tone={invoiceTone(inv.status)}>{s(invoiceStatusLabel(inv.status))}</Chip></>} />
        <InvoiceEditor existing={inv} onSaved={() => setEditing(false)} />
      </>
    );
  }

  const showPdf = pdfPane && inv.pdfAvailable;
  /** Payment and details: right of the lines, or under them when the PDF takes the right half. */
  const sideCards = (
    <>
          {isOpen && (
            <Card tint="success">
              <h2 style={{ marginBottom: '0.3rem' }}>{t('hMarkPaid')}</h2>
              <p className="secondary" style={{ margin: '0 0 0.8rem', fontSize: '0.8rem' }}>{t('recordingStops')}</p>
              <form className="stack md" onSubmit={(e) => { e.preventDefault(); const cents = Math.round(Number(amount.replace(/\./g, '').replace(',', '.') || openCents / 100) * 100); act.mutate({ path: '/payments', json: { paidAt, amountCents: cents } }); }}>
                <Input label={t('paymentDate')} type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
                <Input label={t('amountReceived')} placeholder={formatEur(openCents)} value={amount} onChange={(e) => setAmount(e.target.value)} />
                <Button variant="primary" icon="fas fa-circle-check" block type="submit" disabled={act.isPending}>{t('recordPayment')}</Button>
              </form>
            </Card>
          )}
          <Card>
            <h2>{t('hDetails')}</h2>
            <div className="stack md">
              <Field label={t('customerLabel')} value={<a href={`/customers/${inv.customerId}`}>{inv.customer.name}</a>} />
              <Field label={t('reference')} value={inv.reference ?? '—'} />
              <Field label={t('vatTreatment')} value={s(vatTreatmentLabel(inv.vatTreatment))} />
              <Field label={t('paymentTerms')} value={`${inv.paymentDays} ${t('days')}${inv.company.iban ? ` · IBAN ${inv.company.iban}` : ''}`} />
              <Field label={t('dunningSchedule')} value={inv.dunningPaused ? s('Paused') : inv.nextDunningAt ? `${s('Next run')}: ${formatDate(inv.nextDunningAt, lang)}` : s('Company default')} hint={inv.customer.dunningOverride ? String((inv.customer.dunningOverride as { note?: string }).note ?? 'Customer override') : undefined} />
              {inv.ebInvoiceNumber && <Field label="e-Boekhouden" value={inv.ebInvoiceNumber} mono />}
            </div>
          </Card>
    </>
  );

  return (
    <>
      <PageHeader crumb={t('invDetailCrumb')} title={inv.number} sub={t('invDetailSub', { issued: formatDate(inv.issuedAt, lang), due: formatDate(inv.dueAt, lang) })}
        actions={<>
          {isOpen && <Button icon="fas fa-forward" onClick={() => act.mutate({ path: '/next-stage' })} disabled={act.isPending}>{t('nextStage')}</Button>}
          {isOpen && <Button icon={inv.dunningPaused ? 'fas fa-play' : 'fas fa-pause'} onClick={() => act.mutate({ path: '/pause', json: { paused: !inv.dunningPaused } })}>{inv.dunningPaused ? t('resumeDunning') : t('pauseDunning')}</Button>}
          {inv.status !== 'cancelled' && <Button icon="fas fa-pen" onClick={() => setConfirming('edit')}>{t('editInvoice')}</Button>}
          <Button variant="danger" icon="fas fa-trash-can" onClick={() => setConfirming('delete')}>{t('deleteInvoice')}</Button>
          <Chip tone={invoiceTone(inv.status)}>{s(invoiceStatusLabel(inv.status))}</Chip>
        </>} />
      {dialogs}
      <div className="grid two" style={{ gridTemplateColumns: showPdf ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1.5fr) minmax(0, 1fr)' }}>
        <div className="stack">
          <Card>
            <div className="row" style={{ marginBottom: '0.9rem' }}>
              <BackLink to="/invoices" label={t('backToInvoices')} />
              <div className="spacer" />
              {inv.pdfAvailable && <Button size="sm" icon={pdfPane ? 'fas fa-eye-slash' : 'fas fa-eye'} aria-pressed={pdfPane} onClick={() => setPdfPane(!pdfPane)}>{pdfPane ? t('hidePdf') : t('showPdf')}</Button>}
              <Button size="sm" icon="fas fa-file-pdf" disabled={!inv.pdfAvailable} onClick={() => window.open(companyUrl(`/invoices/${id}/pdf`), '_blank')}>{t('pdf')}</Button>
              <Button size="sm" icon="fas fa-file-code" disabled={!inv.xmlAvailable} onClick={() => window.open(companyUrl(`/invoices/${id}/xml`), '_blank')}>{t('xml')}</Button>
              <Button size="sm" icon="fas fa-paper-plane" onClick={() => act.mutate({ path: '/resend' })} disabled={act.isPending}>{t('resend')}</Button>
              {settings.data?.ebConfigured && !inv.ebInvoiceNumber && <Button size="sm" icon="fas fa-book" onClick={() => setLinking(true)}>{t('ebLink')}</Button>}
            </div>
            <div className="tablewrap">
              <table style={{ minWidth: 480 }}>
                <thead><tr><th className="label" style={{ textAlign: 'left', padding: '0.5rem 0.25rem' }}>{t('colDescription')}</th><th className="label" style={{ textAlign: 'right', padding: '0.5rem 0.25rem' }}>{t('colQty')}</th><th className="label" style={{ textAlign: 'right', padding: '0.5rem 0.25rem' }}>{t('colUnit')}</th><th className="label" style={{ textAlign: 'right', padding: '0.5rem 0.25rem' }}>{t('colVat')}</th><th className="label" style={{ textAlign: 'right', padding: '0.5rem 0.25rem' }}>{t('colTotal')}</th></tr></thead>
                <tbody>
                  {inv.lines.map((l) => (
                    <tr key={l.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                      <td style={{ padding: '0.65rem 0.25rem', fontSize: '0.85rem', color: l.discount ? 'var(--warning)' : undefined }}>{l.description}</td>
                      <td className="mono" style={{ padding: '0.65rem 0.25rem', textAlign: 'right', fontSize: '0.82rem' }}>{l.quantity}</td>
                      <td className="mono" style={{ padding: '0.65rem 0.25rem', textAlign: 'right', fontSize: '0.82rem' }}>{formatEur(l.unitPriceCents)}</td>
                      <td className="secondary" style={{ padding: '0.65rem 0.25rem', textAlign: 'right', fontSize: '0.82rem' }}>{l.vatRate}%</td>
                      <td className="amount" style={{ padding: '0.65rem 0.25rem' }}>{formatEur(l.totalCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="stack" style={{ gap: '0.45rem', marginTop: '0.9rem', paddingTop: '0.9rem', borderTop: '1px solid var(--glass-border)', maxWidth: 320, marginLeft: 'auto' }}>
              {[[t('subtotal'), inv.subtotalCents, false], [`${t('vat')} ${inv.vatTreatment === 'reverse_charge' ? '0% (verlegd)' : ''}`, inv.vatCents, false], [t('totalDue'), inv.totalCents, true]].map(([k, v, strong]) => (
                <div key={String(k)} className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span className="secondary" style={{ fontSize: '0.82rem' }}>{k as string}</span>
                  <span className="mono" style={{ fontWeight: 700, fontSize: strong ? '1.05rem' : '0.86rem', color: strong ? 'var(--heading)' : undefined }}>{formatEur(v as number)}</span>
                </div>
              ))}
              {inv.paidCents > 0 && inv.paidCents < inv.totalCents && <div className="small warning" style={{ color: 'var(--warning)', textAlign: 'right' }}>{t('unpaidPartial', { paid: formatEur(inv.paidCents), total: formatEur(inv.totalCents) })}</div>}
            </div>
            {(inv.introText || inv.noteText) && <div className="stack sm" style={{ marginTop: '1rem' }}>{inv.introText && <div className="textbox">{inv.introText}</div>}{inv.noteText && <div className="textbox">{inv.noteText}</div>}</div>}
          </Card>
          {showPdf && sideCards}
          <Card>
            <h2>{t('hHistory')}</h2>
            <div className="timeline">
              {inv.events.map((e) => (
                <div key={e.id} className="item">
                  <Chip tone={e.tone} dot icon={iconFor(e.type)} />
                  <div><div className="t">{s(e.title)}</div><div className="m">{formatDate(e.createdAt, lang, true)}{e.meta ? ` · ${e.meta}` : ''}</div></div>
                </div>
              ))}
            </div>
          </Card>
        </div>
        {linking && (
          <EbLinkDialog
            invoiceId={id!}
            onClose={() => setLinking(false)}
            onLinked={(data) => { qc.setQueryData(key, data); qc.invalidateQueries({ queryKey: ['invoices'] }); setLinking(false); toast.push(t('done')); }}
          />
        )}
        {showPdf ? (
          <div style={{ position: 'sticky', top: '1rem', alignSelf: 'start' }}>
            <Card>
              <div className="row" style={{ marginBottom: '0.6rem' }}><h2 style={{ margin: 0 }}>{inv.number}.pdf</h2><div className="spacer" /><Button size="sm" icon="fas fa-arrow-up-right-from-square" onClick={() => window.open(companyUrl(`/invoices/${id}/pdf`), '_blank')}>{t('openInNewTab')}</Button></div>
              <PdfFrame url={companyUrl(`/invoices/${id}/pdf`)} title={inv.number} height="calc(100vh - 12rem)" />
            </Card>
          </div>
        ) : <div className="stack">{sideCards}</div>}
      </div>
    </>
  );
}

/** Popup for an invoice with no e-Boekhouden booking: link an existing one, or book a new one. */
function EbLinkDialog({ invoiceId, onClose, onLinked }: { invoiceId: string; onClose: () => void; onLinked: (data: Detail) => void }) {
  const { t, lang } = useI18n();
  const toast = useToast();
  const q = useQuery({ queryKey: ['eb-matches', invoiceId], queryFn: () => capi<EbInvoiceMatch[]>(`/invoices/${invoiceId}/eboekhouden-matches`) });
  const link = useMutation({
    mutationFn: (ebInvoiceId: number) => capi<Detail>(`/invoices/${invoiceId}/link-eboekhouden`, { method: 'POST', json: { ebInvoiceId } }),
    onSuccess: onLinked,
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const create = useMutation({
    mutationFn: () => capi<Detail>(`/invoices/${invoiceId}/push-eboekhouden`, { method: 'POST', json: {} }),
    onSuccess: onLinked,
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const pending = link.isPending || create.isPending;

  return (
    <Modal title={t('ebLinkTitle')} onClose={() => { if (!pending) onClose(); }}>
      <div className="stack md">
        <p className="secondary" style={{ margin: 0, fontSize: '0.86rem', lineHeight: 1.5 }}>{t('ebLinkHint')}</p>
        {q.isLoading && <Loading />}
        {q.isError && <Loading error={q.error as Error} />}
        {q.data && (q.data.length === 0 ? (
          <p className="secondary" style={{ margin: 0, fontSize: '0.84rem' }}>{t('ebLinkNoMatches')}</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="label" style={{ textAlign: 'left', padding: '0.4rem 0.25rem' }}>{t('colEbNumber')}</th>
                <th className="label" style={{ textAlign: 'left', padding: '0.4rem 0.25rem' }}>{t('colEbDate')}</th>
                <th className="label" style={{ textAlign: 'right', padding: '0.4rem 0.25rem' }}>{t('colEbAmount')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.map((m) => (
                <tr key={m.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                  <td className="mono" style={{ padding: '0.5rem 0.25rem', fontSize: '0.85rem' }}>
                    {m.invoiceNumber}
                    {m.exact && <span style={{ marginLeft: '0.5rem' }}><Chip tone="success">{t('ebLinkExact')}</Chip></span>}
                  </td>
                  <td style={{ padding: '0.5rem 0.25rem', fontSize: '0.82rem' }}>{m.date ? formatDate(m.date, lang) : '—'}</td>
                  <td className="mono" style={{ padding: '0.5rem 0.25rem', textAlign: 'right', fontSize: '0.82rem' }}>{m.totalAmount != null ? formatEur(Math.round(m.totalAmount * 100)) : '—'}</td>
                  <td style={{ padding: '0.5rem 0.25rem', textAlign: 'right' }}><Button size="sm" onClick={() => link.mutate(m.id)} disabled={pending}>{t('ebLinkPick')}</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: '0.8rem', paddingTop: '0.7rem', borderTop: '1px solid var(--glass-border)' }}>
          <div className="small muted">{t('ebLinkCreateHint')}</div>
          <Button icon="fas fa-plus" onClick={() => create.mutate()} disabled={pending}>{t('ebLinkCreate')}</Button>
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={pending}>{t('cancel')}</Button>
        </div>
      </div>
    </Modal>
  );
}

function iconFor(type: string): string {
  if (type === 'created') return 'fas fa-file-circle-plus';
  if (type === 'corrected') return 'fas fa-pen';
  if (type === 'imported') return 'fas fa-file-import';
  if (type === 'sent' || type === 'mailed' || type === 'resent') return 'fas fa-paper-plane';
  if (type === 'opened') return 'fas fa-envelope-open-text';
  if (type === 'paid' || type === 'partial_payment') return 'fas fa-circle-check';
  if (type.startsWith('dunning_2')) return 'fas fa-bell';
  if (type.startsWith('dunning')) return 'fas fa-triangle-exclamation';
  if (type === 'overdue') return 'fas fa-clock';
  if (type.startsWith('eb')) return 'fas fa-book';
  return 'fas fa-circle';
}
